import { Button } from "primereact/button";
import { Message } from "primereact/message";

import {
  ConfirmDialog,
  Dialog,
  DialogFooter,
  DialogHeader,
  Dropdown,
  ErrorState,
  ForbiddenState,
  FormField,
  Icon,
  StateMessage,
  Stepper,
} from "@/ds";
import { EtapaDestinoTransferencia } from "../etapa-destino-transferencia";
import { AjustarProjecaoTransferenciaDialog } from "../ajustar-projecao-transferencia-dialog";
import { CartaoFinanceiroTransferencia } from "./cartao-financeiro-transferencia";
import { CartaoProjecaoTransferencia } from "./cartao-projecao-transferencia";
import { ServicosMateriaisSecao } from "../servicos-materiais-secao";
import { EscopoCartao } from "../escopo-cartao";
import { JustificativaField } from "@/shared/components";
import { FecharTooltip, fecharPt } from "../fechar-tooltip";
import {
  CANCELAR_LABEL,
  CARREGANDO_LABEL,
  CENTRO_CUSTO_LABEL,
  CENTRO_CUSTO_PLACEHOLDER,
  CENTRO_CUSTO_REVISAO_LABEL,
  CONCORDO_LABEL,
  CONFIRMAR_TITLE,
  DATAS_COLUNA_DURACAO,
  DATAS_COLUNA_INICIO,
  DATAS_COLUNA_PERIODO,
  DATAS_COLUNA_TERMINO,
  DATAS_TITLE,
  EMBARCACAO_LABEL,
  ETAPAS_LABEL,
  OBRA_LABEL,
  PROXIMA_LABEL,
  REVISAO_SUBTITLE,
  REVISAO_TITLE,
  SAI_DE,
  TIPO_OBRA_LABEL,
  TRANSFERIR_LABEL,
  TRANSFERIR_TITLE,
  VAI_PARA,
  VOLTAR_LABEL,
} from "./transferir-escopo-macro-dialog.constants";
import modal from "../planejamento-controle.modal.module.scss";
import tabela from "../planejamento-controle.tabela.module.scss";
import styles from "./transferir-escopo-macro-dialog.module.scss";
import type {
  ContextoDePara,
  TransferirEscopoMacroDialogViewModel,
} from "./types-transferir-escopo-macro-dialog";

const ROTULO = { label: modal.rotuloCampo };
const PAINEL = { panel: { className: modal.painel } };
const RODAPE_DIALOGO = { footer: { className: modal.dialogoRodape } };
const FECHAR_TESTID = "transferir-fechar-x";
const FECHAR_CONFIRMACAO_TESTID = "transferir-confirmar-fechar-x";
const DIALOGO_PT = { ...RODAPE_DIALOGO, ...fecharPt(FECHAR_TESTID) };
const CONFIRMACAO_PT = { ...RODAPE_DIALOGO, ...fecharPt(FECHAR_CONFIRMACAO_TESTID) };

function Contexto({
  titulo,
  contexto,
  testId,
}: {
  titulo: string;
  contexto: ContextoDePara;
  testId: string;
}) {
  return (
    <section className={styles.contexto} data-testid={testId}>
      <h4 className={styles.contextoTitulo}>{titulo}</h4>
      <dl className={styles.campos}>
        <div>
          <dt>{OBRA_LABEL}</dt>
          <dd>{contexto.obra}</dd>
        </div>
        <div>
          <dt>{EMBARCACAO_LABEL}</dt>
          <dd>{contexto.embarcacao}</dd>
        </div>
        <div>
          <dt>{TIPO_OBRA_LABEL}</dt>
          <dd>{contexto.tipoObra}</dd>
        </div>
        <div>
          <dt>{CENTRO_CUSTO_REVISAO_LABEL}</dt>
          <dd>{contexto.centroCusto}</dd>
        </div>
      </dl>
    </section>
  );
}

export function TransferirEscopoMacroDialogView({
  formId,
  estado,
  errorMessage,
  recarregar,
  etapa,
  etapas,
  cartao,
  avisoSemObras,
  etapaDestino,
  centroCusto,
  destinoErro,
  transferirErro,
  origem,
  paraContexto,
  datas,
  avisoPosicionamento,
  semInicioDestino,
  foraPeriodo,
  proximaDesabilitada,
  servicosMateriais,
  transferirDesabilitado,
  financeiro,
  projecaoCartao,
  avisoSemProjecao,
  ajustar,
  avancar,
  voltar,
  abrirConfirmacao,
  confirmacao,
  cancelar,
  descarte,
}: TransferirEscopoMacroDialogViewModel) {
  const naRevisao = etapa === 2;

  return (
    <>
      <Dialog
        visible
        size="xl"
        className={modal.dialogoFicha}
        onHide={cancelar}
        focusOnShow={false}
        header={<DialogHeader title={TRANSFERIR_TITLE} />}
        pt={DIALOGO_PT}
        footer={
          <DialogFooter
            className={modal.rodape}
            cancelLabel={naRevisao ? VOLTAR_LABEL : CANCELAR_LABEL}
            confirmLabel={naRevisao ? TRANSFERIR_LABEL : PROXIMA_LABEL}
            confirmButtonProps={{
              disabled: naRevisao ? transferirDesabilitado : proximaDesabilitada,
            }}
            onCancel={naRevisao ? voltar : cancelar}
            onConfirm={naRevisao ? abrirConfirmacao : avancar}
            testId="transferir"
          />
        }
        data-testid="transferir-escopo-macro-dialog"
      >
        <FecharTooltip testId={FECHAR_TESTID} />
        <div className={styles.corpo}>
          <EscopoCartao
            {...cartao}
            testIds={{
              cartao: "transferir-cartao",
              projecao: "transferir-projecao",
              nome: "transferir-nome",
              contexto: "transferir-contexto",
            }}
          />

          {estado === "carregando" ? (
            <StateMessage variant="loading" title={CARREGANDO_LABEL} testId="transferir-loading" />
          ) : null}
          {estado === "proibido" ? (
            <ForbiddenState description={errorMessage ?? undefined} testId="transferir-error" />
          ) : null}
          {estado === "erro" ? (
            <ErrorState
              message={errorMessage ?? undefined}
              onRetry={recarregar}
              testId="transferir-error"
            />
          ) : null}

          {estado === "vazio" || estado === "pronto" ? (
            <Stepper.Root aria-label={ETAPAS_LABEL} className={styles.etapas}>
              {etapas.map(({ numero, titulo, situacao, ariaLabel, testId, onSelect }) => (
                <Stepper.Step
                  key={numero}
                  state={situacao}
                  number={numero}
                  title={titulo}
                  ariaLabel={ariaLabel}
                  onSelect={onSelect}
                  testId={testId}
                />
              ))}
            </Stepper.Root>
          ) : null}

          {estado === "vazio" ? (
            <Message
              severity="warn"
              className="p-message-simple p-message-block"
              icon={<Icon name="info-circle" size={18} />}
              text={avisoSemObras}
              data-testid="transferir-sem-obras"
            />
          ) : null}

          {estado === "pronto" && !naRevisao ? (
            <EtapaDestinoTransferencia {...etapaDestino}>
              {destinoErro ? (
                <Message
                  className="p-message-block"
                  severity="error"
                  text={destinoErro}
                  data-testid="transferir-destino-erro"
                />
              ) : null}
              {centroCusto.mostrar ? (
                <div className={styles.destinoCampos}>
                  <FormField.Root required classNames={ROTULO}>
                    <FormField.Label>{CENTRO_CUSTO_LABEL}</FormField.Label>
                    <FormField.Control>
                      <Dropdown
                        appendTo="self"
                        pt={PAINEL}
                        value={centroCusto.value}
                        options={centroCusto.opcoes}
                        placeholder={CENTRO_CUSTO_PLACEHOLDER}
                        onChange={(event) => centroCusto.escolher(event.value)}
                        data-testid="transferir-centro-custo"
                      />
                    </FormField.Control>
                  </FormField.Root>
                </div>
              ) : null}
              {centroCusto.erro ? (
                <Message
                  className="p-message-block"
                  severity="error"
                  text={centroCusto.erro}
                  data-testid="transferir-sem-centro-custo"
                />
              ) : null}
            </EtapaDestinoTransferencia>
          ) : null}

          {estado === "pronto" && !naRevisao ? (
            <ServicosMateriaisSecao {...servicosMateriais} />
          ) : null}

          {estado === "pronto" && naRevisao && paraContexto ? (
            <section className={styles.etapa} data-testid="transferir-revisao">
              <header className={styles.cabecalho}>
                <h3 className={styles.titulo}>{REVISAO_TITLE}</h3>
                <p className={styles.subtitulo}>{REVISAO_SUBTITLE}</p>
              </header>

              <div className={styles.dePara}>
                <Contexto titulo={SAI_DE} contexto={origem} testId="transferir-sai-de" />
                <span className={styles.seta} aria-hidden>
                  {/* O DS não tem arrow-right-arrow-left (Figma 1:54299): PrimeIcons. */}
                  <i className="pi pi-arrow-right-arrow-left" />
                </span>
                <Contexto titulo={VAI_PARA} contexto={paraContexto} testId="transferir-vai-para" />
              </div>

              <section className={styles.datas} data-testid="transferir-datas">
                <h4 className={styles.datasTitulo}>
                  <Icon name="calendar" size={14} />
                  {DATAS_TITLE}
                </h4>
                <div className={tabela.tabelaWrap}>
                  <table className={tabela.tabela}>
                    <thead>
                      <tr>
                        <th scope="col">{DATAS_COLUNA_PERIODO}</th>
                        <th scope="col">{DATAS_COLUNA_INICIO}</th>
                        <th scope="col">{DATAS_COLUNA_TERMINO}</th>
                        <th scope="col">{DATAS_COLUNA_DURACAO}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datas.map((linha) => (
                        <tr key={linha.chave} data-testid={linha.testId}>
                          <td>{linha.periodo}</td>
                          <td>{linha.inicio}</td>
                          <td>{linha.termino}</td>
                          <td>{linha.duracao}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {foraPeriodo ? (
                  <div className={styles.foraPeriodo}>
                    <Message
                      className="p-message-block"
                      severity="warn"
                      text={foraPeriodo.aviso}
                      data-testid="transferir-fora-periodo"
                    />
                    <Button
                      type="button"
                      outlined
                      severity="secondary"
                      label={CONCORDO_LABEL}
                      icon={foraPeriodo.aceite ? "pi pi-check" : undefined}
                      aria-pressed={foraPeriodo.aceite}
                      onClick={() => foraPeriodo.concordar(!foraPeriodo.aceite)}
                      data-testid="transferir-concordo"
                    />
                    {foraPeriodo.erro ? (
                      <Message
                        className="p-message-block"
                        severity="error"
                        text={foraPeriodo.erro}
                        data-testid="transferir-aceite-erro"
                      />
                    ) : null}
                  </div>
                ) : null}

                {transferirErro ? (
                  <Message
                    className="p-message-block"
                    severity="error"
                    text={transferirErro}
                    data-testid="transferir-pendencias-erro"
                  />
                ) : null}

                <Message
                  className="p-message-block"
                  severity={semInicioDestino ? "warn" : "info"}
                  text={avisoPosicionamento}
                  data-testid="transferir-posicionamento-aviso"
                />
              </section>

              <CartaoFinanceiroTransferencia {...financeiro} />
              <CartaoProjecaoTransferencia {...projecaoCartao} />
              {avisoSemProjecao ? (
                <Message
                  className="p-message-block"
                  severity="info"
                  text={avisoSemProjecao}
                  data-testid="transferir-projecao-aviso"
                />
              ) : null}
            </section>
          ) : null}
        </div>
      </Dialog>

      {confirmacao.aberta ? (
        <Dialog
          visible
          size="md"
          className={styles.dialogoConfirmacao}
          onHide={confirmacao.cancelar}
          locked={confirmacao.isSubmitting}
          focusOnShow={false}
          header={<DialogHeader title={CONFIRMAR_TITLE} />}
          pt={CONFIRMACAO_PT}
          footer={
            <DialogFooter
              className={modal.rodape}
              cancelLabel={CANCELAR_LABEL}
              confirmLabel={TRANSFERIR_LABEL}
              formId={formId}
              loading={confirmacao.isSubmitting}
              locked={confirmacao.isSubmitting}
              onCancel={confirmacao.cancelar}
              testId="transferir-confirmar"
            />
          }
          data-testid="transferir-confirmacao-dialog"
        >
          <FecharTooltip testId={FECHAR_CONFIRMACAO_TESTID} />
          <form id={formId} className={styles.confirmacao} onSubmit={confirmacao.enviar} noValidate>
            <EscopoCartao
              {...cartao}
              testIds={{
                cartao: "transferir-confirmar-cartao",
                projecao: "transferir-confirmar-projecao",
                nome: "transferir-confirmar-nome",
                contexto: "transferir-confirmar-contexto",
              }}
            />
            <p className={styles.pergunta} data-testid="transferir-confirmar-mensagem">
              {confirmacao.mensagem}
            </p>
            {confirmacao.avisoEfetivacao ? (
              <Message
                className="p-message-block"
                severity="info"
                text={confirmacao.avisoEfetivacao}
                data-testid="transferir-efetivacao-aviso"
              />
            ) : null}
            <JustificativaField
              labelClassName={modal.rotuloCampo}
              semAsterisco
              value={confirmacao.justificativa}
              onChange={confirmacao.digitarJustificativa}
              error={confirmacao.justificativaError}
              disabled={confirmacao.isSubmitting}
              testIds={{
                campo: "transferir-justificativa",
                contador: "transferir-contador",
                erro: "transferir-justificativa-error",
              }}
            />
            {confirmacao.submitError ? (
              <Message
                className="p-message-block"
                severity="error"
                text={confirmacao.submitError}
                data-testid="transferir-submit-error"
              />
            ) : null}
          </form>
        </Dialog>
      ) : null}

      {ajustar ? <AjustarProjecaoTransferenciaDialog {...ajustar} /> : null}
      {descarte ? <ConfirmDialog {...descarte} /> : null}
    </>
  );
}
